<script lang="ts">
	import { goto } from '$app/navigation';
	import * as Alert from '$lib/components/ui/alert/index.js';
	import { Button } from '$lib/components/ui/button/index.js';
	import { Checkbox } from '$lib/components/ui/checkbox/index.js';
	import { Input } from '$lib/components/ui/input/index.js';
	import { Label } from '$lib/components/ui/label/index.js';
	import { Separator } from '$lib/components/ui/separator/index.js';
	import * as m from '$lib/paraglide/messages.js';
	import { localizeHref } from '@/paraglide/runtime';
	import { pb } from '@/utils/ApiClient';
	import { Eye, EyeOff, ShipWheel } from '@lucide/svelte';
	import CircleAlert from '@lucide/svelte/icons/circle-alert';
	import { ClientResponseError } from 'pocketbase';
	import posthog from 'posthog-js';

	const id = $props.id();

	let username: string = $state('');
	let password: string = $state('');
	let passwordConfirm: string = $state('');
	let email: string = $state('');
	let verificationEmailSent: boolean = $state(false);
	let acceptTerms = $state(false);
	let acceptNewsletter = $state(false);

	let loading: boolean = $state(false);
	let error: ClientResponseError | null = $state(null);
	let showPassword = $state(false);

	// Extract field errors from the error response
	let fieldErrors = $derived(
		error ? (error as any).response?.data || (error as any).originalError?.data?.data || {} : {}
	);

	// Check if passwords match
	let passwordsMatch = $derived(!passwordConfirm || !password || password === passwordConfirm);
	let passwordValid = $derived(password.length >= 10);
	let usernameValid = $derived(username.length >= 3);

	$effect(() => {
		email = email.toLowerCase();
	});

	async function signUp(event: Event) {
		event.preventDefault();
		loading = true;
		error = null;

		try {
			await pb.collection('users').create({
				email,
				username,
				password: password,
				passwordConfirm: passwordConfirm,
				lastLogin: new Date(),
				role: 'user',
				status: 'active',
				acceptNewsletter: acceptNewsletter
			});

			await pb.collection('users').requestVerification(email);
			verificationEmailSent = true;

			posthog.identify(email, { email: email });
			posthog.capture('user_signed_up', { email: email });
		} catch (err) {
			console.error(err);
			error =
				err instanceof ClientResponseError ? err : new ClientResponseError('Registration failed');
		} finally {
			loading = false;
		}
	}

	async function signInWithApple() {
		try {
			loading = true;
			const authData = await pb.collection('users').authWithOAuth2({ provider: 'apple' });

			// Verificar si el usuario está autenticado
			if (pb.authStore.isValid) {
				// Usar setTimeout para asegurar que la redirección ocurra después de que el estado de autenticación se haya actualizado
				setTimeout(() => {
					posthog.identify(authData.record.email, {
						email: authData.record.email,
						id: authData.record.id
					});
					posthog.capture('user_signed_up', { email: authData.record.email });
					goto(localizeHref('/cuenta'));
				}, 100);
			} else {
				console.error('La autenticación no fue exitosa');
				// Manejar el caso de autenticación fallida
				error = new ClientResponseError(
					'No se pudo completar el inicio de sesión con Apple. Por favor, intenta de nuevo.'
				);
			}
		} catch (err) {
			console.error('Error durante la autenticación con Apple:', err);
			error =
				err instanceof ClientResponseError
					? err
					: new ClientResponseError(
							'Ocurrió un error durante el inicio de sesión con Apple. Por favor, intenta de nuevo.'
						);
		} finally {
			loading = false;
		}
	}
	async function signInWithGoogle() {
		try {
			const authData = await pb.collection('users').authWithOAuth2({ provider: 'google' });
			posthog.identify(authData.record.email, {
				email: authData.record.email,
				id: authData.record.id
			});
			posthog.capture('user_signed_up', { email: email });
			goto(localizeHref('/cuenta'));
		} catch (err) {
			console.error(err);
			error =
				err instanceof ClientResponseError
					? err
					: new ClientResponseError(
							'Ocurrió un error durante el inicio de sesión con Google. Por favor, intenta de nuevo.'
						);
		} finally {
			loading = false;
		}
	}
</script>

<svelte:head>
	<title>Zarpar | Crea tu cuenta</title>
	<meta
		name="description"
		content="Regístrate en Zarpar y únete a la mayor comunidad de alquiler de barcos entre particulares. Publica tu barco o encuentra la embarcación perfecta para navegar."
	/>
</svelte:head>

<div class="w-full md:mt-16 lg:grid lg:min-h-[600px]">
	{#if !verificationEmailSent}
		<div class="flex items-center justify-center py-4 md:py-12">
			<div class="mx-auto grid w-lg gap-4 p-2 md:p-0">
				<div class="grid gap-2 text-center">
					<h1 class="text-3xl font-bold">{m.ideal_just_ibex_thrive()}</h1>
					<p class="text-muted-foreground">{m.funny_curly_donkey_enchant()}</p>
				</div>
				<div class="mb-2 flex w-full gap-2">
					<button
						onclick={() => {
							signInWithApple();
						}}
						class="flex flex-1 items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus-visible:ring-transparent"
					>
						<svg
							class="h-5 w-5"
							viewBox="0 0 24 24"
							fill="#000000"
							version="1.1"
							xmlns="http://www.w3.org/2000/svg"
							xmlns:xlink="http://www.w3.org/1999/xlink"
							xml:space="preserve"
						>
							<g>
								<g>
									<path
										d="M15.769,0c0.053,0,0.106,0,0.162,0c0.13,1.606-0.483,2.806-1.228,3.675c-0.731,0.863-1.732,1.7-3.351,1.573
									   c-0.108-1.583,0.506-2.694,1.25-3.561C13.292,0.879,14.557,0.16,15.769,0z"
									></path>
									<path
										d="M20.67,16.716c0,0.016,0,0.03,0,0.045c-0.455,1.378-1.104,2.559-1.896,3.655c-0.723,0.995-1.609,2.334-3.191,2.334
									   c-1.367,0-2.275-0.879-3.676-0.903c-1.482-0.024-2.297,0.735-3.652,0.926c-0.155,0-0.31,0-0.462,0
									   c-0.995-0.144-1.798-0.932-2.383-1.642c-1.725-2.098-3.058-4.808-3.306-8.276c0-0.34,0-0.679,0-1.019
									   c0.105-2.482,1.311-4.5,2.914-5.478c0.846-0.52,2.009-0.963,3.304-0.765c0.555,0.086,1.122,0.276,1.619,0.464
									   c0.471,0.181,1.06,0.502,1.618,0.485c0.378-0.011,0.754-0.208,1.135-0.347c1.116-0.403,2.21-0.865,3.652-0.648
									   c1.733,0.262,2.963,1.032,3.723,2.22c-1.466,0.933-2.625,2.339-2.427,4.74C17.818,14.688,19.086,15.964,20.67,16.716z"
									></path>
								</g>
							</g></svg
						>
						<span class="text-sm leading-6 font-semibold">Apple</span>
					</button>
					<button
						onclick={() => {
							signInWithGoogle();
						}}
						class="flex flex-1 items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus-visible:ring-transparent"
					>
						<svg class="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
							<path
								d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
								fill="#EA4335"
							></path>
							<path
								d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
								fill="#4285F4"
							></path>
							<path
								d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
								fill="#FBBC05"
							></path>
							<path
								d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
								fill="#34A853"
							></path>
						</svg>
						<span class="text-sm leading-6 font-semibold">Google</span>
					</button>
				</div>
				<p class="text-muted-foreground text-center text-xs">
					Al continuar, aceptas los <a
						href={localizeHref('/terminos-y-condiciones')}
						target="_blank"
						class="underline">Términos y Condiciones</a
					>
					y la
					<a href={localizeHref('/politica-de-privacidad/')} target="_blank" class="underline"
						>Política de Privacidad</a
					>.
				</p>
				<Separator />

				<form onsubmit={signUp} class="grid gap-4">
					<div class="grid gap-2">
						<Label for="username-{id}">{m.seemly_mealy_wallaby_feast()}</Label>
						<div class="relative rounded-md shadow-xs">
							<div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
								@
							</div>
							<Input
								bind:value={username}
								class="pl-8 text-base md:text-sm"
								id="username-{id}"
								type="text"
								placeholder="user123"
								required
								disabled={loading}
								aria-invalid={fieldErrors.username || (username && !usernameValid)
									? true
									: undefined}
								aria-describedby={fieldErrors.username || (username && !usernameValid)
									? 'username-error-{id}'
									: undefined}
							/>
						</div>
						{#if fieldErrors.username?.message}
							<p id="username-error-{id}" class="text-destructive text-sm">
								{fieldErrors.username.message}
							</p>
						{:else if username && !usernameValid}
							<p id="username-error-{id}" class="text-destructive text-sm">
								El nombre de usuario debe tener al menos 3 caracteres
							</p>
						{/if}
					</div>
					<div class="grid gap-2">
						<Label for="email-{id}">Email</Label>
						<Input
							bind:value={email}
							id="email-{id}"
							type="email"
							placeholder="hola@ejemplo.com"
							required
							class="text-base md:text-sm"
							disabled={loading}
							aria-invalid={fieldErrors.email ? true : undefined}
							aria-describedby={fieldErrors.email ? 'email-error-{id}' : undefined}
						/>
						{#if fieldErrors.email?.message}
							<p id="email-error-{id}" class="text-destructive text-sm">
								{fieldErrors.email.message === 'Value must be unique'
									? 'Email ya está en uso, intenta iniciar sesión'
									: fieldErrors.email.message}
							</p>
						{/if}
					</div>
					<div class="grid gap-2">
						<div class="flex items-center">
							<Label for="password-{id}">{m.livid_weary_turkey_cuddle()}</Label>
						</div>
						<div class="relative">
							<Input
								bind:value={password}
								id="password-{id}"
								type={showPassword ? 'text' : 'password'}
								required
								class="pr-10 text-base md:text-sm"
								disabled={loading}
								aria-invalid={fieldErrors.password || (password && !passwordValid)
									? true
									: undefined}
								aria-describedby={fieldErrors.password || (password && !passwordValid)
									? 'password-error-{id}'
									: undefined}
							/>
							<button
								type="button"
								class="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
								onclick={() => (showPassword = !showPassword)}
								disabled={loading}
							>
								{#if showPassword}
									<EyeOff class="size-4" />
								{:else}
									<Eye class="size-4" />
								{/if}
							</button>
						</div>
						{#if fieldErrors.password?.message}
							<p id="password-error-{id}" class="text-destructive text-sm">
								{fieldErrors.password.message}
							</p>
						{:else if password && !passwordValid}
							<p id="password-error-{id}" class="text-destructive text-sm">
								La contraseña debe tener al menos 10 caracteres
							</p>
						{/if}
					</div>
					<div class="grid gap-2">
						<div class="flex items-center">
							<Label for="repeat-password-{id}">Repetir Contraseña</Label>
						</div>
						<div class="relative">
							<Input
								bind:value={passwordConfirm}
								id="repeat-password-{id}"
								type={showPassword ? 'text' : 'password'}
								required
								class="pr-10 text-base md:text-sm"
								disabled={loading}
								aria-invalid={fieldErrors.passwordConfirm || !passwordsMatch ? true : undefined}
								aria-describedby={fieldErrors.passwordConfirm || !passwordsMatch
									? 'repeat-password-error-{id}'
									: undefined}
							/>
							<button
								type="button"
								class="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
								onclick={() => (showPassword = !showPassword)}
								disabled={loading}
							>
								{#if showPassword}
									<EyeOff class="size-4" />
								{:else}
									<Eye class="size-4" />
								{/if}
							</button>
						</div>
						{#if fieldErrors.passwordConfirm?.message}
							<p id="repeat-password-error-{id}" class="text-destructive text-sm">
								{fieldErrors.passwordConfirm.message}
							</p>
						{:else if passwordConfirm && !passwordsMatch}
							<p id="repeat-password-error-{id}" class="text-destructive text-sm">
								Las contraseñas no coinciden
							</p>
						{/if}
					</div>
					<div class="flex items-start gap-2">
						<Checkbox bind:checked={acceptTerms} id="acceptTerms" required />
						<label for="acceptTerms" class="text-sm select-none">
							Acepto los <a
								href={localizeHref('/terminos-y-condiciones')}
								target="_blank"
								class="underline">Términos y Condiciones</a
							>
							y la
							<a href={localizeHref('/politica-de-privacidad/')} target="_blank" class="underline"
								>Política de Privacidad</a
							>
						</label>
					</div>
					<div class="flex items-start gap-2">
						<Checkbox bind:checked={acceptNewsletter} id="acceptNewsletter" />
						<label for="acceptNewsletter" class="text-sm select-none">
							Quiero recibir noticias y ofertas sobre alquiler de barcos
						</label>
					</div>
					<Button
						type="submit"
						class="w-full"
						disabled={loading ||
							!acceptTerms ||
							!passwordsMatch ||
							!passwordValid ||
							!usernameValid}
					>
						{#if loading}
							<ShipWheel class="mr-4 animate-spin" />{m.patient_knotty_horse_tap()}
						{:else}
							{m.fair_away_moth_gaze()}
						{/if}
					</Button>
				</form>
				{#if error && !fieldErrors.email && !fieldErrors.username && !fieldErrors.password && !fieldErrors.passwordConfirm}
					<Alert.Root variant="destructive">
						<CircleAlert class="h-4 w-4" />
						<Alert.Description
							>{error.message || 'Ha ocurrido un error al crear la cuenta'}</Alert.Description
						>
					</Alert.Root>
				{/if}
				<div class="mt-4 text-center text-sm">
					{m.lost_sweet_fish_sail()}
					<a href={localizeHref('/iniciar-sesion')} class="underline">
						{m.such_just_nils_hurl()}
					</a>
				</div>
			</div>
		</div>
	{:else}
		<div class="mx-auto mt-12 mb-24 w-xl px-4 md:px-0">
			<div class="relative">
				<div class="rounded-md bg-green-50 p-4 sm:mt-4">
					<div class="flex justify-center">
						<div class="shrink-0">
							<svg
								class="h-5 w-5 text-green-400"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden="true"
							>
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
									clip-rule="evenodd"
								></path>
							</svg>
						</div>
						<div class="z-10 ml-3">
							<h3 class="text-sm font-medium text-green-800">{m.red_busy_cowfish_tap()}</h3>
							<div class="z-10 mt-2 text-sm text-green-700">
								<p>
									{m.gray_best_elk_pet()}
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
			<img
				class="mx-auto -mt-12 w-full justify-center sm:max-w-xl"
				src="/img/112766-celebrate.gif"
				alt="welcome animation banner"
			/>
		</div>
	{/if}
</div>
